import React, { useEffect, useMemo, useState } from "react";
import { Card, Button, Modal, Form } from "react-bootstrap";
import axios from "axios";
import Select from "react-select";
import { API_URL } from "../../../config";
import "./NoticeBoard.css";

function NoticeBoard() {
  const [notices, setNotices] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sendToAll, setSendToAll] = useState(false);
  const [show, setShow] = useState(false);
  const [newNotice, setNewNotice] = useState({
    title: "",
    description: "",
    category: "General",
  });
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [sortBy, setSortBy] = useState("Newest");
  const [showArchived, setShowArchived] = useState(false);
  const [metaLoadingIds, setMetaLoadingIds] = useState([]);

  const getNoticeKey = (notice) => notice?._id || notice?.NotificationId;

  const inferPriority = (notice) => {
    if (notice?.category === "Task") return "High";
    if (notice?.category === "Holiday") return "Medium";
    return "Low";
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/getAllNotifications`);
      const uniqueNotices = Array.from(
        new Map(res.data.notifications.map((n) => [n.NotificationId, n])).values()
      );
      setNotices(uniqueNotices);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/getemployeeData`);
      setUsers(res.data.employees || res.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchUsers();
  }, []);

  const handleSaveNotice = async () => {
    const { title, description, category } = newNotice;
    if (!title || !description) return alert("Title and Message are required");

    const body = {
      title,
      body: description,
      category,
      allUsers: sendToAll,
      userIds: selectedUsers.map((u) => u.value),
    };

    try {
      setLoading(true);
      await axios.post(`${API_URL}/api/sendNotification`, body);
      alert("Notification sent successfully!");
      setShow(false);
      setNewNotice({ title: "", description: "", category: "General" });
      setSelectedUsers([]);
      setSendToAll(false);
      fetchNotifications();
    } catch (error) {
      if (error.response && error.response.status === 409) {
        alert("This notification already exists!");
      } else {
        alert("Failed to send notification");
      }
    } finally {
      setLoading(false);
    }
  };

  const userOptions = users.map((user) => ({
    value: user._id,
    label: user.ename || user.name,
  }));

  const handleDeleteNotice = async (id) => {
    if (!window.confirm("Are you sure you want to delete this notice?")) return;

    try {
      await axios.delete(`${API_URL}/api/deleteNotice/${id}`);
      alert("Notice deleted successfully!");
      fetchNotifications();
    } catch (error) {
      alert("Failed to delete notice");
    }
  };

  const patchNoticeMeta = async (id, payload) => {
    setMetaLoadingIds((prev) => [...prev, id]);
    try {
      const { data } = await axios.patch(`${API_URL}/api/updateNoticeMeta/${id}`, payload);
      const updated = data?.notice;
      if (!updated) return;
      setNotices((prev) =>
        prev.map((notice) => (notice._id === id ? { ...notice, ...updated } : notice))
      );
    } catch {
      alert("Failed to update notice");
    } finally {
      setMetaLoadingIds((prev) => prev.filter((item) => item !== id));
    }
  };

  const togglePin = async (notice) => {
    await patchNoticeMeta(notice._id, { isPinned: !Boolean(notice.isPinned) });
  };

  const toggleArchive = async (notice) => {
    await patchNoticeMeta(notice._id, { isArchived: !Boolean(notice.isArchived) });
  };

  const cyclePriority = async (notice, basePriority) => {
    const order = ["Low", "Medium", "High"];
    const current = notice.priority || basePriority || "Low";
    const currentIndex = order.indexOf(current);
    const next = order[(currentIndex + 1) % order.length];
    await patchNoticeMeta(notice._id, { priority: next });
  };

  const filteredNotices = useMemo(() => {
    const term = searchText.trim().toLowerCase();

    const enriched = notices.map((notice) => {
      const id = getNoticeKey(notice);
      const priority = notice.priority || inferPriority(notice);
      const isPinned = Boolean(notice.isPinned);
      const isArchived = Boolean(notice.isArchived);
      const metaBusy = metaLoadingIds.includes(notice._id);

      return {
        ...notice,
        localId: id,
        localPriority: priority,
        isPinned,
        isArchived,
        metaBusy,
      };
    });

    const filtered = enriched.filter((notice) => {
      if (showArchived ? !notice.isArchived : notice.isArchived) return false;

      const matchesCategory = categoryFilter === "All" || notice.category === categoryFilter;
      if (!matchesCategory) return false;

      const matchesPriority = priorityFilter === "All" || notice.localPriority === priorityFilter;
      if (!matchesPriority) return false;

      if (!term) return true;
      const combinedText = `${notice.title || ""} ${notice.body || ""} ${notice.category || ""}`.toLowerCase();
      return combinedText.includes(term);
    });

    const sorted = [...filtered].sort((a, b) => {
      if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;

      if (sortBy === "Oldest") {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }

      if (sortBy === "Title A-Z") {
        return (a.title || "").localeCompare(b.title || "");
      }

      if (sortBy === "Priority") {
        const rank = { High: 3, Medium: 2, Low: 1 };
        return (rank[b.localPriority] || 0) - (rank[a.localPriority] || 0);
      }

      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    return sorted;
  }, [
    notices,
    searchText,
    categoryFilter,
    priorityFilter,
    sortBy,
    showArchived,
    metaLoadingIds,
  ]);

  const stats = useMemo(() => {
    const total = notices.length;
    const active = notices.filter((n) => !n.isArchived).length;
    const pinned = notices.filter((n) => n.isPinned).length;
    const archived = notices.filter((n) => n.isArchived).length;
    return { total, active, pinned, archived };
  }, [notices]);

  const categories = ["All", "General", "Holiday", "Task"];
  const priorities = ["All", "Low", "Medium", "High"];
  const sortOptions = ["Newest", "Oldest", "Title A-Z", "Priority"];

  const getCategoryClass = (category) => {
    if (category === "Holiday") return "notice-chip holiday";
    if (category === "Task") return "notice-chip task";
    return "notice-chip general";
  };

  const getPriorityClass = (priority) => {
    if (priority === "High") return "notice-chip priority-high";
    if (priority === "Medium") return "notice-chip priority-medium";
    return "notice-chip priority-low";
  };

  const clearFilters = () => {
    setSearchText("");
    setCategoryFilter("All");
    setPriorityFilter("All");
    setSortBy("Newest");
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: "#111827",
      borderColor: state.isFocused ? "#f59e0b" : "rgba(148, 163, 184, 0.35)",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(245, 158, 11, 0.2)" : "none",
      borderRadius: "10px",
      minHeight: "42px",
      color: "#f8fafc",
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "#111827",
      border: "1px solid rgba(148, 163, 184, 0.25)",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#92400e" : state.isFocused ? "#3f3f46" : "#111827",
      color: "#f8fafc",
      cursor: "pointer",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "rgba(245, 158, 11, 0.2)",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#f8fafc",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#f8fafc",
      ":hover": {
        backgroundColor: "#b45309",
        color: "#fff",
      },
    }),
    input: (base) => ({ ...base, color: "#f8fafc" }),
    singleValue: (base) => ({ ...base, color: "#f8fafc" }),
    placeholder: (base) => ({ ...base, color: "#94a3b8" }),
  };

  return (
    <div className="notice-page">
      <section className="notice-hero">
        <div>
          <p className="notice-overline">Admin Communication Center</p>
          <h2 className="notice-title">Notice Board Dashboard</h2>
          <p className="notice-subline">Create announcements, target recipients, and track message flow</p>
        </div>
        <Button className="notice-add-btn" onClick={() => setShow(true)}>
          Add Notice
        </Button>
      </section>

      <section className="notice-stats notice-stats-four">
        <div className="notice-stat-card">
          <p>Total Notices</p>
          <h3>{stats.total}</h3>
        </div>
        <div className="notice-stat-card">
          <p>Active</p>
          <h3>{stats.active}</h3>
        </div>
        <div className="notice-stat-card">
          <p>Pinned</p>
          <h3>{stats.pinned}</h3>
        </div>
        <div className="notice-stat-card">
          <p>Archived</p>
          <h3>{stats.archived}</h3>
        </div>
      </section>

      <section className="notice-toolbar notice-toolbar-extended">
        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search by title, message, or category"
          className="notice-search"
        />

        <Form.Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="notice-filter">
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              Category: {cat}
            </option>
          ))}
        </Form.Select>

        <Form.Select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="notice-filter">
          {priorities.map((priority) => (
            <option key={priority} value={priority}>
              Priority: {priority}
            </option>
          ))}
        </Form.Select>

        <Form.Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="notice-filter">
          {sortOptions.map((option) => (
            <option key={option} value={option}>
              Sort: {option}
            </option>
          ))}
        </Form.Select>

        <Button className="notice-tool-btn" onClick={() => setShowArchived((prev) => !prev)}>
          {showArchived ? "Show Active" : "Show Archived"}
        </Button>

        <Button className="notice-tool-btn muted" onClick={clearFilters}>
          Clear Filters
        </Button>
      </section>

      {filteredNotices.length === 0 ? (
        <div className="notice-empty">
          <p className="mb-1">No notices match your current view.</p>
          <small>{showArchived ? "Try switching to active notices." : "Try clearing filters or changing sort."}</small>
        </div>
      ) : (
        filteredNotices.map((notice) => (
          <Card key={notice.NotificationId} className="notice-card">
            <Card.Body>
              <div className="notice-card-head">
                <div>
                  <Card.Title className="notice-card-title">
                    {notice.title}
                    {notice.isPinned ? <span className="notice-chip pinned">Pinned</span> : null}
                    {notice.isArchived ? <span className="notice-chip archived">Archived</span> : null}
                    <span className={getCategoryClass(notice.category)}>{notice.category}</span>
                    <span className={getPriorityClass(notice.localPriority)}>{notice.localPriority} Priority</span>
                  </Card.Title>
                  <small className="notice-date">{new Date(notice.createdAt).toLocaleString()}</small>
                  <div className="notice-recipient">
                    <strong>Recipients:</strong>{" "}
                    {notice.recipientNames ? notice.recipientNames.join(", ") : notice.allUsers ? "All Employees" : "-"}
                  </div>
                </div>
                <div className="notice-card-actions">
                  <Button
                    size="sm"
                    className="notice-action-btn"
                    onClick={() => togglePin(notice)}
                    disabled={notice.metaBusy}
                  >
                    {notice.isPinned ? "Unpin" : "Pin"}
                  </Button>
                  <Button
                    size="sm"
                    className="notice-action-btn"
                    onClick={() => cyclePriority(notice, notice.localPriority)}
                    disabled={notice.metaBusy}
                  >
                    Cycle Priority
                  </Button>
                  <Button
                    size="sm"
                    className="notice-action-btn muted"
                    onClick={() => toggleArchive(notice)}
                    disabled={notice.metaBusy}
                  >
                    {notice.isArchived ? "Unarchive" : "Archive"}
                  </Button>
                  <Button size="sm" className="notice-delete-btn" onClick={() => handleDeleteNotice(notice._id)}>
                    Delete
                  </Button>
                </div>
              </div>
              <Card.Text className="notice-body">{notice.body}</Card.Text>
            </Card.Body>
          </Card>
        ))
      )}

      <Modal show={show} onHide={() => setShow(false)} centered className="notice-modal">
        <Modal.Header closeButton className="notice-modal-header">
          <Modal.Title>Create New Notice</Modal.Title>
        </Modal.Header>

        <Modal.Body className="notice-modal-body">
          <Form>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Send to All Users"
                checked={sendToAll}
                onChange={(e) => setSendToAll(e.target.checked)}
                className="notice-check"
              />
            </Form.Group>

            {!sendToAll && (
              <Form.Group className="mb-3">
                <Form.Label>Select User(s)</Form.Label>
                <Select
                  options={userOptions}
                  isMulti
                  value={selectedUsers}
                  onChange={setSelectedUsers}
                  styles={selectStyles}
                />
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={newNotice.title}
                onChange={(e) => setNewNotice({ ...newNotice, title: e.target.value })}
                className="notice-input"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newNotice.description}
                onChange={(e) => setNewNotice({ ...newNotice, description: e.target.value })}
                className="notice-input"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={newNotice.category}
                onChange={(e) => setNewNotice({ ...newNotice, category: e.target.value })}
                className="notice-input"
              >
                <option value="General">General</option>
                <option value="Holiday">Holiday</option>
                <option value="Task">Priority Task</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer className="notice-modal-footer">
          <Button className="notice-cancel-btn" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button className="notice-save-btn" onClick={handleSaveNotice} disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default NoticeBoard;
